import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 8;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(() => {
    this.#updatePosition();
    this.#updateMaxHeight();
  });

  #updateMaxHeight = () => {
    // Quick measurement - content is already rendered
    const naturalHeight = this.scrollHeight;
    this.style.setProperty('--panel-height', `${naturalHeight}px`);
  };

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
      this.#updateMaxHeight();
    }
  }, 100);

  #handleDetailsToggle = (event) => {
    const details = event.target;
    if (details.open) {
      // Update height when panel opens
      this.#updateMaxHeight();
    }
  };

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    // Listen for details toggle events
    const details = this.closest('details');
    if (details) {
      details.addEventListener('toggle', this.#handleDetailsToggle);
    }

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#updateMaxHeight();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
    
    // Clean up details toggle listener
    const details = this.closest('details');
    if (details) {
      details.removeEventListener('toggle', this.#handleDetailsToggle);
    }
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
